import React, { FC } from 'react';
import { BasketFinancePaymentSelectionProps } from '@components/v2/BasketFinancePaymentSelection';
import useTranslations from '@hooks/useTranslations';
import { BUDGET_TYPE_FINANCE } from '../../../services';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { useRouter } from 'next/router';
import { Link as LinkV2 } from '@components/v2/link';
import Link from '@components/Link';
import { FINANCE_DETAIL_VIEW, isBrandAC, isBrandAP, isBrandDS, isBrandOV, isMarketGB } from '../../../constants/main';
import { useGetPaymentKeywords } from '@components/OfferListItem/utils/getOfferKeywords';
import { usePrices } from '@hooks/usePriceCalculator';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import PriceSuffix from '@components/PriceSuffix';
import { Icons } from '@components/Icon';
import {
    getFinanceDetailText,
    getFinanceDetailView,
    getMonthlyPriceObjByType,
    renderPromotionalText,
} from '@utils/Price.utils';
import { StockFormatedPriceType } from '../../../services/stock/models/stockItem.price.model';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { useSummaryDataPromise } from '@components/OfferListItem/hooks/useSummaryDataPromise';
import { useFinanceWidgetDuck } from '@hooks/useFinanceWidgetDuck';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { IconTemplate } from '@components/Icon/IconTemplate';
import {useJourneyType} from "@hooks/useJourneyType";

const BasketFinancePaymentSelectionTemplate: FC<BasketFinancePaymentSelectionProps> = ({
    isSelected,
    onClick,
    className,
    handleFinanceWidgetBtnClick,
}) => {
    const { t } = useTranslations();

    const router = useRouter();

    const { formatMoney } = useMoneyFormatter();

    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const { finance } = usePrices();

    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { financeQuote, leasysQuote, globalTexts, pricesList } = useFinanceWidgetDuck();
    const { isFinance, isRent } = useJourneyType();

    const showAnnualMileage = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_ANNUAL_MILEAGE);
    const showTanAndTaeg = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_TAN_AND_TAEG);

    const {
        totalPrice: monthlyPrice,
        financeProperties: { deposit, duration, annualMileage, apr, tin, finalPayment },
    } = finance;

    const promoText =
        financeQuote?.primaryText ?? (carDetails?.extraFields?.promotionalText || carDetails?.promotionalText);

    const paymentKeywords = useGetPaymentKeywords(deposit, duration, annualMileage, apr);
    const financeDetailView = getFinanceDetailView(carDetails.prices, StockFormatedPriceType.EMPLOYEE);
    const financeDetailText = getFinanceDetailText(
        carDetails.prices,
        StockFormatedPriceType.EMPLOYEE,
        globalTexts,
        pricesList,
        carDetails.externalId || carDetails.vehicleId
    );
    const amountRounding = getMonthlyPriceObjByType(carDetails.prices, StockFormatedPriceType.EMPLOYEE)?.amountRounding;

    const { processCalculateSummaryDataPromise } = useSummaryDataPromise({
        carConfigId: carDetails._id ?? carDetails.externalId,
        token: isFinance && !isRent ? financeQuote?.token ?? currentDeal?.financeQuoteId : null,
        carConfiguration: carDetails,
    });

    return (
        <div className={className}>
            <button
                className={`section ${isSelected ? 'selected' : ''}`}
                onClick={() => onClick(BUDGET_TYPE_FINANCE)}
                data-testid="TESTING_SELECTION_FINANCE"
            >
                <div className="inner">
                    <span className="title">
                        <span className={`icon ${isSelected ? 'selected' : ''}`}>
                            <span className={isSelected ? 'selected' : ''} />
                        </span>{' '}
                        {t('basket.payment.finance.title')}
                    </span>
                    <span className="description">{t('basket.payment.finance.description')}</span>
                    <div className="summary">
                        <div className="totalPrice">
                            {formatMoney(monthlyPrice, false, '', amountRounding)}
                            <PriceSuffix checkPrioritizedTranslation isMonthlyPrice />
                            {financeDetailView === FINANCE_DETAIL_VIEW.ICON && (
                                <IconTemplate
                                    className="infoIcon"
                                    name={
                                        isBrandAC || isBrandAP || isBrandDS || (isBrandOV && !isMarketGB)
                                            ? Icons.InfoCircle
                                            : Icons.Info
                                    }
                                    width={16}
                                    height={16}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        processCalculateSummaryDataPromise();
                                    }}
                                />
                            )}
                        </div>
                        <span className="totalLabel">
                            {!isMarketGB &&
                                renderPromotionalText(promoText, t('price.pattern'), deposit, null, null, null, null, {
                                    apr,
                                })}
                        </span>
                        {financeDetailView === FINANCE_DETAIL_VIEW.LINK &&
                            (isBrandAC ? (
                                <LinkV2
                                    txt={financeDetailText}
                                    onClick={(event: Event) => {
                                        event.stopPropagation();
                                        processCalculateSummaryDataPromise();
                                    }}
                                    isUnderline
                                    style={{ marginTop: '-10px' }}
                                />
                            ) : (
                                <Link
                                    label={financeDetailText}
                                    onClick={(event: Event) => {
                                        event.stopPropagation();
                                        processCalculateSummaryDataPromise();
                                    }}
                                    marginBottom={20}
                                    secondary
                                />
                            ))}
                        {isSelected && (
                            <>
                                <div className="otherPrices">
                                    <div className="otherPrice withOrder">
                                        <span>{t('finance.firstRental')}</span>
                                        <span>
                                            <strong>{formatMoney(deposit)}</strong>
                                        </span>
                                    </div>
                                    {showTanAndTaeg && (
                                        <div className="otherPrice">
                                            <span>{t('finance.tan')}</span>
                                            <span>{tin?.toString()?.replace('.', ',')}%</span>
                                        </div>
                                    )}
                                    <div className="otherPrice withOrder">
                                        <span>{t('finance.duration')}</span>
                                        <span>
                                            <strong>
                                                {duration} {t('finance.duration.unit')}
                                            </strong>
                                        </span>
                                    </div>
                                    {showTanAndTaeg && (
                                        <div className="otherPrice">
                                            <span>{t('finance.taeg')}</span>
                                            <span>{apr?.toString()?.replace('.', ',')}%</span>
                                        </div>
                                    )}
                                    <div className="otherPrice withOrder">
                                        <span>{t('finance.annualMileage')}</span>
                                        <span>
                                            <strong>
                                                {annualMileage} {t('finance.annualMileage.units')}
                                            </strong>
                                        </span>
                                    </div>
                                    {showTanAndTaeg && (
                                        <div className="otherPrice">
                                            <span>{t('finance.balloon')}</span>
                                            {finalPayment && finalPayment > 0 && (
                                                <span>{formatMoney(finalPayment)}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p
                                    className="description"
                                    dangerouslySetInnerHTML={{ __html: t('finance.legal.text.long') }}
                                />
                                <Link
                                    label={t('basket.payment.selectedFinance.personalize')}
                                    onClick={handleFinanceWidgetBtnClick}
                                    withArrowIcon
                                    primary={!isBrandAC}
                                    secondary={isBrandAC}
                                    marginBottom={20}
                                />
                                <div className="legalText">
                                    {isMarketGB ? (
                                        <p>{paymentKeywords}</p>
                                    ) : (
                                        <p>
                                            <span
                                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                    'finance.legal.text1'
                                                )}
                                            />
                                            <br />
                                            <span
                                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                    'finance.legal.text2'
                                                )}
                                            />
                                            <br />
                                            <span
                                                dangerouslySetInnerHTML={getNotEscapedTranslationMarkup(
                                                    'finance.legal.text3'
                                                )}
                                            />
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </button>
        </div>
    );
};

export default BasketFinancePaymentSelectionTemplate;
